import { useState, useEffect } from "react";
import { G, ADMIN_API, adminInput, IcoUsers } from "../../design/tokens";

export default function UsersModule({ authHeader, canWrite, loggedUser, onTokenRefresh }) {
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [formData, setFormData] = useState({ username: "", password: "", permissions: { kb: {}, leads: {}, users: {}, logs: {} } });
  const [formErr, setFormErr]  = useState("");
  const [saving, setSaving]    = useState(false);

  const emptyPerms = { leads: { read: false, write: false }, chatbot: { read: false, write: false }, portfolio: { read: false, write: false }, users: { read: false, write: false }, logs: { read: false, write: false }, kb: { read: false, write: false }, audit: { read: false, write: false } };
  const MOD_COLORS = { leads: "#7C3AED", chatbot: "#06B6D4", portfolio: "#F59E0B", users: "#059669", logs: "#D97706", kb: "#2563EB", audit: "#E11D48" };
  const MOD_LABELS = { leads: "Leads", chatbot: "ChatBot", portfolio: "Portfolio", users: "Users", logs: "Login Logs", kb: "Knowledge Base", audit: "Audit Trail" };

  const loadUsers = () => {
    setLoading(true);
    fetch(`${ADMIN_API}/users`, { headers: authHeader })
      .then(r => r.json())
      .then(d => { setUsers(d.users || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { loadUsers(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditUser(null);
    setFormData({ username: "", password: "", permissions: { ...emptyPerms } });
    setFormErr(""); setShowForm(true);
  };

  const openEdit = (u) => {
    setEditUser(u.username);
    const perms = {};
    Object.keys(emptyPerms).forEach(m => { perms[m] = { read: !!(u.permissions?.[m]?.read), write: !!(u.permissions?.[m]?.write) }; });
    setFormData({ username: u.username, password: "", permissions: perms });
    setFormErr(""); setShowForm(true);
  };

  const togglePerm = (mod, type) => setFormData(prev => ({ ...prev, permissions: { ...prev.permissions, [mod]: { ...prev.permissions[mod], [type]: !prev.permissions[mod][type] } } }));

  const saveUser = async () => {
    setFormErr(""); setSaving(true);
    const body = { permissions: formData.permissions };
    if (!editUser) { body.username = formData.username; body.password = formData.password; }
    else if (formData.password) body.password = formData.password;
    const res  = await fetch(editUser ? `${ADMIN_API}/users/${encodeURIComponent(editUser)}` : `${ADMIN_API}/users`, { method: editUser ? "PUT" : "POST", headers: authHeader, body: JSON.stringify(body) });
    const data = await res.json();
    setSaving(false);
    if (!res.ok) return setFormErr(data.error || "Failed");
    if (editUser && editUser === loggedUser && formData.password && onTokenRefresh) {
      const newToken = btoa(`${editUser}:${formData.password}`);
      onTokenRefresh(newToken);
    }
    setShowForm(false); loadUsers();
  };

  const deleteUser = async (username) => {
    if (!window.confirm(`Delete user "${username}"?`)) return;
    await fetch(`${ADMIN_API}/users/${encodeURIComponent(username)}`, { method: "DELETE", headers: authHeader });
    loadUsers();
  };

  return (
    <div style={{ padding: "28px 32px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
        <div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 600, color: "#fff" }}>Users</div>
          <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, color: "rgba(255,255,255,0.3)", marginTop: 4 }}>Manage admin access and module permissions</div>
        </div>
        {canWrite && <button onClick={openCreate} style={{ padding: "10px 22px", borderRadius: 10, border: "none", background: G.btnGrad, color: "#fff", fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", boxShadow: G.btnShadow }}>+ Add User</button>}
      </div>

      {showForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "linear-gradient(145deg, #0F2548 0%, #060E1E 100%)", border: "1px solid rgba(37,99,235,0.25)", borderRadius: 20, padding: "40px 36px", width: 480, boxShadow: "0 32px 100px rgba(0,0,0,0.7)" }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 600, color: "#fff", marginBottom: 26 }}>{editUser ? `Edit — ${editUser}` : "Add New User"}</div>

            {!editUser && (
              <>
                <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: "rgba(255,255,255,0.35)", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6 }}>Email Address (login username)</div>
                <input type="email" value={formData.username} onChange={e => setFormData(p => ({ ...p, username: e.target.value }))} placeholder="user@example.com" style={adminInput({ marginBottom: 16 })} />
              </>
            )}

            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: "rgba(255,255,255,0.35)", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6 }}>{editUser ? "New Password (blank to keep)" : "Password"}</div>
            <input type="password" value={formData.password} onChange={e => setFormData(p => ({ ...p, password: e.target.value }))} placeholder="••••••••" style={adminInput({ marginBottom: 20 })} />

            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: "rgba(255,255,255,0.35)", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 12 }}>Module Permissions</div>
            <div style={{ display: "grid", gap: 8, marginBottom: 24 }}>
              {Object.keys(emptyPerms).map(mod => (
                <div key={mod} style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 7, height: 7, borderRadius: "50%", background: MOD_COLORS[mod] }} />
                    <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, color: "rgba(255,255,255,0.65)" }}>{MOD_LABELS[mod]}</span>
                  </div>
                  <div style={{ display: "flex", gap: 20 }}>
                    {["read","write"].map(type => (
                      <label key={type} style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer" }}>
                        <div onClick={() => togglePerm(mod, type)} style={{ width: 36, height: 20, borderRadius: 10, background: formData.permissions[mod]?.[type] ? G.btnGrad : "rgba(255,255,255,0.08)", position: "relative", transition: "all 0.2s", cursor: "pointer", flexShrink: 0 }}>
                          <div style={{ position: "absolute", top: 2, left: formData.permissions[mod]?.[type] ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 4px rgba(0,0,0,0.4)" }} />
                        </div>
                        <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, color: "rgba(255,255,255,0.35)", textTransform: "capitalize" }}>{type}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {formErr && <div style={{ color: "#f87171", fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, marginBottom: 16, padding: "10px 14px", background: "rgba(248,113,113,0.08)", borderRadius: 9, border: "1px solid rgba(248,113,113,0.18)" }}>{formErr}</div>}

            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={saveUser} disabled={saving} style={{ flex: 1, padding: "12px", borderRadius: 10, border: "none", background: G.btnGrad, color: "#fff", fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", boxShadow: G.btnShadow, opacity: saving ? 0.7 : 1 }}>
                {saving ? "Saving…" : (editUser ? "Update User" : "Create User")}
              </button>
              <button onClick={() => setShowForm(false)} style={{ padding: "12px 20px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.08)", background: "transparent", color: "rgba(255,255,255,0.4)", fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, cursor: "pointer" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ fontFamily: "'Space Grotesk', sans-serif", color: "rgba(255,255,255,0.25)", fontSize: 14, padding: "60px 0", textAlign: "center" }}>Loading…</div>
      ) : users.length === 0 ? (
        <div style={{ textAlign: "center", padding: "70px 0" }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: "rgba(37,99,235,0.08)", border: "1px solid rgba(37,99,235,0.15)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", color: "rgba(147,197,253,0.4)" }}><IcoUsers /></div>
          <div style={{ fontFamily: "'Space Grotesk', sans-serif", color: "rgba(255,255,255,0.25)", fontSize: 14 }}>No sub-users yet.{canWrite ? " Add one above." : ""}</div>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {users.map(u => (
            <div key={u.username} style={{ background: G.glass, border: `1px solid ${G.glassBrd}`, borderRadius: 14, padding: "18px 22px", display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 44, height: 44, borderRadius: 13, background: "linear-gradient(135deg, rgba(37,99,235,0.25), rgba(124,58,237,0.25))", border: "1px solid rgba(37,99,235,0.25)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Space Grotesk', sans-serif", fontSize: 18, color: "#93C5FD", fontWeight: 700, flexShrink: 0 }}>
                {u.username[0].toUpperCase()}
              </div>
              <div style={{ minWidth: 120 }}>
                <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 14, color: "#fff", fontWeight: 600 }}>{u.username}</div>
                <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, color: "rgba(255,255,255,0.25)", marginTop: 3 }}>Since {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "—"}</div>
              </div>
              <div style={{ display: "flex", gap: 6, flex: 1, flexWrap: "wrap" }}>
                {Object.keys(emptyPerms).map(mod => {
                  const r = u.permissions?.[mod]?.read, w = u.permissions?.[mod]?.write;
                  return (
                    <div key={mod} style={{ padding: "4px 10px", borderRadius: 7, background: (r||w) ? `${MOD_COLORS[mod]}14` : "rgba(255,255,255,0.04)", border: `1px solid ${(r||w) ? MOD_COLORS[mod]+"3A" : "rgba(255,255,255,0.06)"}` }}>
                      <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 8, color: "rgba(255,255,255,0.3)", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 1 }}>{mod}</div>
                      <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, fontWeight: 600, color: (r||w) ? MOD_COLORS[mod] : "rgba(255,255,255,0.15)" }}>
                        {r && w ? "R+W" : r ? "Read" : w ? "Write" : "None"}
                      </div>
                    </div>
                  );
                })}
              </div>
              {canWrite && (
                <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                  <button onClick={() => openEdit(u)} style={{ padding: "7px 16px", borderRadius: 8, border: "1px solid rgba(37,99,235,0.3)", background: "rgba(37,99,235,0.08)", color: "#93C5FD", fontFamily: "'Space Grotesk', sans-serif", fontSize: 12, cursor: "pointer" }}>Edit</button>
                  <button onClick={() => deleteUser(u.username)} style={{ padding: "7px 16px", borderRadius: 8, border: "1px solid rgba(248,113,113,0.2)", background: "rgba(248,113,113,0.07)", color: "#f87171", fontFamily: "'Space Grotesk', sans-serif", fontSize: 12, cursor: "pointer" }}>Delete</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
